import { X } from "lucide-react";

// YouTube draws its end screen — the recommended-video cards — over the last
// stretch of a video. Cover it for a little longer than it lasts.
const END_WINDOW = 22;

// How far the iframe is grown past the top and bottom of its box. The video
// keeps its 16:9 inside that taller frame, so the extra height is letterbox;
// cropping it away removes YouTube's title bar and its logo/suggestion row
// while leaving every pixel of the picture. Ported from the killerplayer
// PlayerCore, where 60 is the proven default.
const CROP = 60;

/**
 * The video surface.
 *
 * It shows whichever of the two players is currently the audio source, so
 * turning this on never reloads anything and never interrupts the track —
 * only the styling of the host changes. What it must not do is look like
 * YouTube: each iframe is cropped as described above and never receives a
 * pointer event, so there is nothing to click and no way to end up on
 * youtube.com. Transport stays in the app's own player bar.
 */
export default function VideoStage({
  mountARef,
  mountBRef,
  activeKey,
  visible,
  poster,
  title,
  playing,
  current,
  duration,
  onClose,
}) {
  const covered = !playing || (duration > 0 && duration - current <= END_WINDOW);

  return (
    <div
      data-testid="video-stage"
      aria-hidden={!visible}
      className={
        visible
          ? "fixed left-1/2 -translate-x-1/2 bottom-[146px] md:bottom-[96px] z-40 w-[92vw] max-w-[420px] md:max-w-[480px] aspect-video rounded-xl overflow-hidden bg-black shadow-2xl ring-1 ring-white/10"
          : "fixed w-px h-px -left-[9999px] -top-[9999px] opacity-0 pointer-events-none overflow-hidden"
      }
    >
      {/* The crop, holding both players stacked. Never unmounted and never
          moved between parents: rebuilding this subtree would rebuild the
          iframes and stop the music. Only the spare's opacity changes. */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute left-0 w-full pointer-events-none"
          style={{ top: `-${CROP}px`, height: `calc(100% + ${CROP * 2}px)` }}
        >
          <div
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{ opacity: activeKey === "a" ? 1 : 0, zIndex: activeKey === "a" ? 2 : 1 }}
          >
            <div ref={mountARef} className="w-full h-full pointer-events-none" />
          </div>
          <div
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{ opacity: activeKey === "b" ? 1 : 0, zIndex: activeKey === "b" ? 2 : 1 }}
          >
            <div ref={mountBRef} className="w-full h-full pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Paused, or inside the end-screen window. */}
      {visible && covered && (
        <div
          data-testid="video-stage-cover"
          className="absolute inset-0 z-10 bg-black bg-cover bg-center"
          style={poster ? { backgroundImage: `url(${poster})` } : undefined}
        >
          <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" />
          {title && (
            <p className="absolute bottom-3 left-4 right-10 text-xs text-white/70 truncate">{title}</p>
          )}
        </div>
      )}

      {visible && (
        <button
          data-testid="video-stage-close"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-2 right-2 z-20 p-1.5 rounded-full bg-black/60 text-white/80 hover:text-white hover:bg-black/80 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
